import { css } from '@emotion/react';
import { math } from 'polished';

import Mixin from '../../../util/mixin';
import { MediaBreakpointUp } from './_breakpoints.mixin';
import { divide } from '../vendor/_rfs.mixin';
import { compareValues, percentage } from '../_functions';

/// Grid system
//
// Generate semantic grid columns with these mixins.

export class MakeContainer extends Mixin {
  include($) {
    const [gutter = $.gridGutterWidth] = this.args;

    // Extract numeric value from gutter (e.g., "30px" -> 30)
    const gutterValue = parseFloat(gutter);
    const gutterUnit = gutter.replace(/[\d.]/g, ''); // Extract unit (e.g., "px")

    return css`
      width: 100%;
      padding-right: ${math(`${gutterValue} * 0.5`)}${gutterUnit};
      padding-left: ${math(`${gutterValue} * 0.5`)}${gutterUnit};
      margin-right: auto;
      margin-left: auto;
    `;
  }
}

export class MakeRow extends Mixin {
  include($) {
    const [gutter = $.gridGutterWidth] = this.args;

    // Extract numeric value from gutter (e.g., "30px" -> 30)
    const gutterValue = parseFloat(gutter);
    const gutterUnit = gutter.replace(/[\d.]/g, ''); // Extract unit (e.g., "px")

    return css`
      display: flex;
      flex-wrap: wrap;
      margin-right: ${math(`-${gutterValue} * 0.5`)}${gutterUnit};
      margin-left: ${math(`-${gutterValue} * 0.5`)}${gutterUnit};
    `;
  }
}

// For each breakpoint, define the maximum width of the container in a media query
export class MakeContainerMaxWidths extends Mixin {
  include($) {
    const [maxWidths = $.containerMaxWidths, breakpoints = $.gridBreakpoints] = this.args;

    return css(Object.entries(maxWidths).map(([breakpoint, containerMaxWidth]) => {
      return new MediaBreakpointUp(breakpoint, breakpoints).include($, css`
            max-width: ${containerMaxWidth}px;
      `);
    }));
  }
};


export class MakeColReady extends Mixin {
  include($) {
    const [gutter = $.gridGutterWidth] = this.args;

    // Extract numeric value from gutter (e.g., "30px" -> 30)
    const gutterValue = parseFloat(gutter);
    const gutterUnit = gutter.replace(/[\d.]/g, ''); // Extract unit (e.g., "px")

    return css`
      position: relative;
      // Prevent columns from becoming too narrow when at smaller grid tiers by
      // always setting \`width: 100%;\`. This works because we use \`flex\` values
      // later on to override this initial width.
      width: 100%;
      padding-right: ${math(`${gutterValue} * 0.5`)}${gutterUnit};
      padding-left: ${math(`${gutterValue} * 0.5`)}${gutterUnit};
    `;
  }
}

export class MakeCol extends Mixin {
  include($) {
    const [size, columns = $.gridColumns] = this.args;
    const value = percentage(divide(size, columns));

    // Add a `max-width` to ensure content within each column does not blow out
    // the width of the column. Applies to IE10+ and Firefox. Chrome and Safari
    // do not appear to require this.
    return css`
      flex: 0 0 ${value};
      max-width: ${value};
    `;
  }
};

export class MakeColAuto extends Mixin {
  include() {
    return css`
      flex: 0 0 auto;
      width: auto;
      max-width: 100%; // Reset earlier grid tiers
    `;
  }
}

export class MakeColOffset extends Mixin {
  include($) {
    const [size, columns = $.gridColumns] = this.args;
    const value = percentage(divide(size, columns));

    return css`
      margin-left: ${compareValues(value, "==", 0) ? 0 : value};
    `;
  }
};

// Row columns
//
// Specify on a parent element(e.g., .row) to force immediate children into NN
// numberof columns. Supports wrapping to new lines, but does not do a Masonry
// style grid.
export class RowCols extends Mixin {
  include() {
    const [count] = this.args;
    const value = divide("100%", count)
    return css`
      > * {
        flex: 0 0 ${value};
        max-width: ${value};
      }
    `;
  }
};
